import { useEffect, useState } from "react";
import { Form, Button, Card, Row, Col, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getBlogById, createBlog, updateBlog } from "../../api/blog";
import ImageUploader from "../../components/upload-image";
import { toast } from "react-toastify";
import slugify from "slugify";
import { ChevronLeft, Save } from "lucide-react";

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    banner: "",
    content: "",
    slug: "",
    meta_seo: {
      meta_title: "",
      meta_keyword: "",
      meta_desc: "",
      meta_slug_url: "",
    },
  });

  useEffect(() => {
    if (isEdit) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      const data = await getBlogById(id);
      setFormData(data);
    } catch (error) {
      toast.error("Failed to fetch blog details");
      navigate("/blogs");
    } finally {
      setFetching(false);
    }
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    const slug = slugify(title, { lower: true, strict: true });
    setFormData((prev) => ({
      ...prev,
      title,
      slug: isEdit ? prev.slug : slug, // Only auto-update slug on create
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("meta_")) {
      setFormData((prev) => ({
        ...prev,
        meta_seo: { ...prev.meta_seo, [name]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleQuillChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  const handleBannerChange = (res) => {
    setFormData((prev) => ({ ...prev, banner: res ? res.data.file_url : "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug) {
      toast.warn("Title and Slug are required");
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await updateBlog(id, formData);
        toast.success("Blog updated successfully");
      } else {
        await createBlog(formData);
        toast.success("Blog created successfully");
      }
      navigate("/blogs");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 bg-light/30">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button
          variant="light"
          onClick={() => navigate("/blogs")}
          className="rounded-circle p-2 shadow-sm border-0"
        >
          <ChevronLeft size={20} />
        </Button>
        <div>
          <h2 className="fw-bold mb-0 text-[#1a1a4e]">{isEdit ? "Edit Blog" : "Add New Blog"}</h2>
          <p className="text-muted small mb-0">Fill in the details for your blog post</p>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm rounded-3xl mb-4">
              <Card.Body className="p-4">
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold text-[#1a1a4e]">Blog Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder="Enter blog title"
                    className="rounded-xl border-gray-200 py-2.5"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold text-[#1a1a4e]">URL Slug</Form.Label>
                  <Form.Control
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    placeholder="blog-url-slug"
                    className="rounded-xl border-gray-200 py-2.5 bg-light"
                    required
                  />
                  <Form.Text className="text-muted">
                    This will be the URL of the blog: /blogs/{formData.slug || "slug"}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold text-[#1a1a4e]">Short Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter short description for card and SEO"
                    className="rounded-xl border-gray-200"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold text-[#1a1a4e]">Blog Content</Form.Label>
                  <div className="quill-wrapper rounded-xl border border-gray-200 overflow-hidden">
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={handleQuillChange}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ["bold", "italic", "underline", "strike"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["link", "image", "video"],
                          ["clean"],
                        ],
                      }}
                      style={{ height: "400px", marginBottom: "50px" }}
                    />
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="border-0 shadow-sm rounded-3xl mb-4">
              <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0">
                <h5 className="fw-bold text-[#1a1a4e] mb-0">Banner Image</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <ImageUploader
                  imageUrl={formData.banner}
                  onChange={handleBannerChange}
                  title="Banner"
                  imgStyle={{ width: "100%", height: "200px" }}
                />
                <p className="text-muted small mt-2">
                  Recommended size: 1200x600px
                </p>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-3xl mb-4">
              <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0">
                <h5 className="fw-bold text-[#1a1a4e] mb-0">SEO Settings</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold">Meta Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="meta_title"
                    value={formData.meta_seo.meta_title}
                    onChange={handleInputChange}
                    placeholder="SEO Title"
                    className="rounded-xl border-gray-200"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold">Meta Keywords</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="meta_keyword"
                    value={formData.meta_seo.meta_keyword}
                    onChange={handleInputChange}
                    placeholder="keywords, separated, by, commas"
                    className="rounded-xl border-gray-200"
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small fw-semibold">Meta Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="meta_desc"
                    value={formData.meta_seo.meta_desc}
                    onChange={handleInputChange}
                    placeholder="Short SEO summary"
                    className="rounded-xl border-gray-200"
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <div className="d-grid gap-2">
              <Button
                type="submit"
                className="d-flex align-items-center justify-content-center gap-2 py-3 rounded-2xl border-0 shadow-lg transition-all"
                style={{ backgroundColor: "#1a1a4e" }}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Save size={20} />
                    {isEdit ? "Update Blog" : "Publish Blog"}
                  </>
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default BlogDetails;
